import {useConfig} from '@aboutyou/config';
import {Sponsored} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_sponsored.types';
import {HeroBrandsSection_BrandElement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {Video as VideoType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/video.types';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {
  TrackComponentParamsType,
  useTrackOriginClick,
  useTrackProductTileGallerySwipe,
} from '@aboutyou/tracking';
import {
  BlurryBackground,
  COLORS,
  Image,
  ImageRatio,
  UnstyledLinkLinaria,
  VideoOrFallbackImage,
  mediaTabletAndUp,
  tabletAndUpLinaria,
  useGetAriaLabelFromUrl,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useSplitFirstProductIds} from '../../../../hooks/useSplitFirstProductIds';
import {HeroContentSwitch} from './HeroContentSwitch';

export interface HeroContentProps extends HeroBrandsSection_BrandElement {
  className?: string;
  activeBrandIndex: number;
  onAddToBasketCtaClick?: ({
    productId,
    locationId,
    couponPromotionId,
  }: {
    productId: number;
    locationId?: string;
    couponPromotionId?: string;
  }) => void;
  onClick: VoidFunction;
  streamPosition: number;
  trackProductListSwipeEvent?: boolean;
  uniqueKey: string;
  trackComponentConfig: TrackComponentParamsType;
  sponsored?: Sponsored;
  coverLandscapeVideo?: VideoType;
  coverPortraitVideo?: VideoType;
}

export const HeroContent: React.FC<HeroContentProps> = ({
  activeBrandIndex,
  brand,
  className,
  content,
  coverPortraitImage,
  coverLandscapeImage,
  coverPortraitVideo,
  coverLandscapeVideo,
  discoverMore,
  header,
  onAddToBasketCtaClick,
  onClick,
  promotionTracker,
  streamPosition,
  trackProductListSwipeEvent = false,
  uniqueKey,
  trackComponentConfig,
  sponsored,
}) => {
  const {device} = useConfig();
  const history = useHistory();
  const trackSwipe = useTrackProductTileGallerySwipe();
  const trackOriginClick = useTrackOriginClick();
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();
  const targetLink =
    content?.$case === 'products' ? content.products.link.url : undefined;

  const {linkWithoutFirstProductIds, fullLink} =
    useSplitFirstProductIds(targetLink);

  const handleTargetLinkClick: React.MouseEventHandler = event => {
    const originId = (promotionTracker.locationId ??
      trackComponentConfig.location_id) as originClick;
    trackOriginClick(originId)();
    if (fullLink && fullLink !== linkWithoutFirstProductIds) {
      event.preventDefault();
      history.push(fullLink);
    }
  };

  const [image, video] =
    device === 'desktop'
      ? [coverPortraitImage, coverPortraitVideo]
      : [coverLandscapeImage, coverLandscapeVideo];

  const defaultAspetRatio = device === 'desktop' ? '3:3.5' : '3:1';
  const imageRatio: ImageRatio = image?.aspectRatio
    ? `${image.aspectRatio.width}:${image.aspectRatio.height}`
    : defaultAspetRatio;

  const onProductListSwipe = () => {
    if (!trackProductListSwipeEvent) {
      return;
    }

    trackSwipe({
      sponsored: false,
      name: 'product_lane_swipe',
      locationId: promotionTracker.locationId ?? '',
      sortingChannel: '',
      itemId: NaN,
      ...(promotionTracker.creativeName === 'ad' && {
        creativeName: 'ad',
        promotionId: promotionTracker.promotionId,
      }),
    });
  };

  const sliderVideoOrImage = (
    <StyledVideoOrFallbackImage
      $useFallbackBackgroundColor={!image.url}
      video={video}
      image={
        image.url ? (
          <Image
            importance="high"
            loading="eager"
            src={image.url}
            alt={image.description}
            ratio={imageRatio}
            widths={[400, 800, 1000]}
            breakpoints={[{columns: device === 'desktop' ? 2 : 1}]}
          />
        ) : null
      }
    />
  );

  return (
    <Wrapper onClick={onClick} className={className}>
      <ContentWrapper>
        {targetLink ? (
          <TargetLink
            to={linkWithoutFirstProductIds || targetLink}
            aria-label={getAriaLabelFromUrl(targetLink)}
            onClick={handleTargetLinkClick}
            data-testid="hero-brand-cover-link"
          >
            {sliderVideoOrImage}
          </TargetLink>
        ) : (
          <>{sliderVideoOrImage}</>
        )}

        <BlurryBackground imageUrl={image?.url} blur={30} scale={4} />

        <HeroContentSwitch
          content={content}
          image={image}
          header={header}
          brand={brand}
          discoverMore={discoverMore}
          onClick={onClick}
          onAddToBasketCtaClick={onAddToBasketCtaClick}
          promotionTracker={promotionTracker}
          streamPosition={streamPosition}
          activeBrandIndex={activeBrandIndex}
          onProductListSwipe={onProductListSwipe}
          uniqueKey={uniqueKey}
          trackComponentConfig={trackComponentConfig}
          sponsored={sponsored}
        />
      </ContentWrapper>
    </Wrapper>
  );
};

const TargetLink = styled(UnstyledLinkLinaria)`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  width: 100%;
`;

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  text-align: center;

  ${tabletAndUpLinaria(`
    --desktop-image-width: 35%;
    flex-direction: row;
    border-radius: 16px 0px 0px 16px;
    padding-bottom: 0;
  `)}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;

  ${tabletAndUpLinaria(`
    flex-direction: row;
    align-items: center;
 `)}
`;

const StyledVideoOrFallbackImage = styled(VideoOrFallbackImage)<{
  $useFallbackBackgroundColor: boolean;
}>`
  position: relative;
  width: 100%;
  height: var(--cover-image-height);
  z-index: 1;
  background-color: ${props =>
    props.$useFallbackBackgroundColor ? COLORS.Black : 'transparent'};

  padding-top: ${props => (props.$useFallbackBackgroundColor ? '180px' : 0)};

  // a black gradient to ensure the follow button and the mWeb logo remains visible on light images.
  &::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.2) 100%,
      rgba(0, 0, 0, 0)
    );
    z-index: 1;
  }

  > img,
  > video {
    height: 100%;
    object-fit: cover;
  }

  @media ${mediaTabletAndUp} {
    width: var(--desktop-image-width);
    height: 100%;
    border-radius: 16px 0px 0px 16px;
    overflow: hidden;
    padding-block: ${props => (props.$useFallbackBackgroundColor ? '30vh' : 0)};

    &::before {
      // The first overlay improves the visibility of the follow-brand and the sponsored icon, and the second makes the brand logo visible.
      background: linear-gradient(
          to right,
          rgba(0, 0, 0, 0.2) 50%,
          rgba(0, 0, 0, 0)
        ),
        linear-gradient(
          to top right,
          rgba(0, 0, 0, 0.2) 0%,
          rgba(0, 0, 0, 0) 60%
        );
    }

    > img,
    > video {
      mask-image: linear-gradient(
        to right,
        rgba(0, 0, 0, 1) 70%,
        rgba(0, 0, 0, 0)
      );
    }
  }
`;
